import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface RatingComparisonChartProps {
  selfReview: number | null | undefined;
  predicted: number | null | undefined;
  actual: number | null | undefined;
}

const BAR_COLORS = {
  self: "#7b1fa2",
  predicted: "#1976d2",
  actual: "#2e7d32",
};

const formatLabel = (value: number | null): string => (value === null ? "N/A" : value.toFixed(2));

/**
 * Compares the employee's self-review, the AI's predicted manager rating, and
 * the manager's actual rating side by side — a genuinely new comparison (does
 * the AI land closer to self-perception or to the manager's actual call?)
 * rather than repeating the predicted-vs-actual numbers already shown above
 * this card.
 */
const RatingComparisonChart: React.FC<RatingComparisonChartProps> = ({ selfReview, predicted, actual }) => {
  const data = [
    { name: "Self-Review", value: selfReview ?? null, color: BAR_COLORS.self },
    { name: "AI Predicted", value: predicted ?? null, color: BAR_COLORS.predicted },
    { name: "Manager Actual", value: actual ?? null, color: BAR_COLORS.actual },
  ];

  const hasAnyData = data.some((d) => d.value !== null && d.value !== undefined);

  if (!hasAnyData) {
    return (
      <Typography variant="body2" color="text.secondary">
        No rating data available to compare.
      </Typography>
    );
  }

  const chartData = data.map((d) => ({ ...d, plotValue: d.value ?? 0 }));

  return (
    <Box sx={{ width: "100%", height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis domain={[0, 5]} />
          <Tooltip
            formatter={(_value: number, _name, item) => {
              const datum = item?.payload as (typeof chartData)[number] | undefined;
              return [formatLabel(datum?.value ?? null), "Rating"];
            }}
          />
          <Bar dataKey="plotValue" radius={[4, 4, 0, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              formatter={(value: number | null) => formatLabel(value)}
              style={{ fontSize: 12, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RatingComparisonChart;
